import React from 'react';
import { Helmet } from 'react-helmet';

const DeckleMatchingGuide = () => (
    <section className="section">
        <Helmet>
            <title>What is Deckle Matching? A Complete Guide for Paper Manufacturers | DecklePro Blog</title>
            <meta name="description" content="Learn what deckle matching is, why it matters for paper manufacturers, and how DecklePro software can reduce trim waste and improve production efficiency." />
            <meta name="keywords" content="deckle matching, paper manufacturing, trim waste, DecklePro, paper roll cutting, yield improvement, deckle optimization, slitting, parent roll, knife rules, GSM, reporting" />
            <link rel="canonical" href="https://www.anmatics.com/blogs/deckle-matching-guide" />
            <meta property="og:title" content="What is Deckle Matching? A Complete Guide for Paper Manufacturers" />
            <meta property="og:description" content="Discover the importance of deckle matching in paper manufacturing and how DecklePro software helps reduce waste and boost efficiency." />
            <meta property="og:type" content="article" />
            <meta property="og:url" content="https://www.anmatics.com/blogs/deckle-matching-guide" />
        </Helmet>
        <div className="container" style={{ maxWidth: '820px' }}>
            <header className="mb-2 pb-3 border-bottom" style={{ background: 'none', boxShadow: 'none' }}>
                <h2 className="fw-bold mb-2" style={{ color: 'var(--anmatics-primary)' }}>
                    What is Deckle Matching? A Complete Guide for Paper Manufacturers
                </h2>
                <p className="text-secondary"><strong>Published By Anmatics Technologies</strong></p>
            </header>
            <article itemScope itemType="https://schema.org/Article">
                <p>
                    Deckle matching is a crucial optimization technique in the paper manufacturing process that helps reduce trim waste and improve production efficiency during the cutting of paper rolls. In this guide, we’ll explain what deckle matching is, why it matters, and how modern software like DecklePro can make a significant impact on your bottom line.
                </p>

                <h4 className="mt-4" style={{ color: 'var(--anmatics-primary-hover)' }}>What is Deckle Matching?</h4>
                <p>
                    Deckle matching refers to the process of aligning multiple paper orders with different roll widths to fit within a fixed machine deckle (cutting width). The goal is to minimize the amount of paper that is trimmed and wasted during slitting.
                </p>

                <h6 className="mt-4">Example:</h6>
                <div className="bg-light border rounded-3 p-3 mb-3">
                    <p className="mb-0">
                        If your machine deckle is <strong>2500mm</strong> and you have orders of <strong>600mm</strong>, <strong>700mm</strong>, and <strong>800mm</strong> widths, deckle matching helps you combine them in a way that comes as close to 2500mm as possible-reducing leftover material and boosting yield.
                    </p>
                </div>

                <h4 className="mt-4" style={{ color: 'var(--anmatics-primary-hover)' }}>Why is Deckle Matching Important?</h4>
                <ul className="mb-4">
                    <li><strong>Reduces Trim Waste:</strong> Less material loss during production</li>
                    <li><strong>Improves Yield:</strong> Maximizes use of each parent roll</li>
                    <li><strong>Saves Time:</strong> Faster planning and slitting setup</li>
                    <li><strong>Lowers Costs:</strong> Less raw material waste = higher profit</li>
                </ul>

                <h4 className="mt-4" style={{ color: 'var(--anmatics-primary-hover)' }}>Traditional vs. Software-Based Deckle Matching</h4>
                <p>
                    Traditionally, deckle planning is done manually or using spreadsheets, which is time-consuming and prone to error. Modern solutions like <a href="/product-details" style={{ color: 'var(--anmatics-primary)', textDecoration: 'underline' }}>DecklePro</a> automate the matching process using smart algorithms, allowing planners to instantly generate the most optimal cutting combinations.
                </p>

                <h4 className="mt-4" style={{ color: 'var(--anmatics-primary-hover)' }}>How DecklePro Helps</h4>
                <ul className="mb-4">
                    <li>Smart matching algorithm to group orders</li>
                    <li>Trim waste percentage calculator</li>
                    <li>Custom machine deckle & knife rules</li>
                    <li>Real-time reporting and stock planning</li>
                </ul>

                <h4 className="mt-4" style={{ color: 'var(--anmatics-primary-hover)' }}>Conclusion</h4>
                <p>
                    Deckle matching is more than a technical process-it's a powerful tool for operational excellence. Whether you're managing one machine or ten, adopting an intelligent tool like DecklePro can help you minimize waste, improve delivery timelines, and cut costs.
                </p>

                <div className="alert alert-primary mt-4" style={{ background: 'var(--anmatics-primary)', color: '#fff' }}>
                    <strong>Want to learn how DecklePro can optimize your operations?</strong> <a href="/contact" style={{ color: '#fff', textDecoration: 'underline' }}>Book a free demo today!</a>
                </div>
            </article>
        </div>
    </section>
);
export default DeckleMatchingGuide;